import type { GfeSummary } from "./gfe";
import { z } from "zod";
import type { BoulevardFlagKind, BoulevardReceiptView } from "./boulevard";
import type { ChartTemplateKey } from "./charts";
import type { Problem } from "./engine";
import type { FormNeed, HealthSummary } from "./forms";
import type { PaymentView, SavedCardView } from "./payments";

export const APPOINTMENT_STATUSES = ["unconfirmed", "confirmed", "arrived", "active", "completed", "cancelled", "no_show"] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];
export const STATUS_LABELS: Record<AppointmentStatus, string> = {
  unconfirmed: "Unconfirmed",
  confirmed: "Confirmed",
  arrived: "Arrived",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};
/** The front desk board's columns, in order. */
export const BOARD_STATUSES = ["unconfirmed", "confirmed", "arrived", "active", "completed"] as const;
export const isOpenStatus = (s: AppointmentStatus) => s !== "cancelled" && s !== "no_show";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Clinic-local "2026-10-08T14:30". */
export const localDateTimeSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/, "Use a date and time");

/** One service on a visit, with the add-ons that ride on it (an add-on adds to its service, e.g. the drip). */
export const visitServiceSchema = z.object({
  serviceId: z.string().uuid(),
  addOnIds: z.array(z.string().uuid()).max(20).default([]),
});
export type VisitServiceRequest = z.infer<typeof visitServiceSchema>;
const servicesField = z.array(visitServiceSchema).min(1, "Pick a service").max(8);

/** Older callers send one service as `serviceId` and `addOnIds`: read as a list of one. */
export function oneService(v: unknown) {
  if (!v || typeof v !== "object" || "services" in v || !("serviceId" in v)) return v;
  const o = v as { serviceId: unknown; addOnIds?: unknown };
  return { ...o, services: [{ serviceId: o.serviceId, addOnIds: o.addOnIds ?? [] }] };
}

const guestBase = z.object({
  services: servicesField,
  staffId: z.string().uuid().nullable().default(null),
  resourceIds: z.array(z.string().uuid()).max(10).default([]),
  /** Staff: why it's booked against the visit rules (two drips, or a medication twice). */
  servicesReason: z.string().trim().max(300).optional(),
});
export const guestRequestSchema = z.preprocess(oneService, guestBase);
export type GuestRequest = z.infer<typeof guestBase>;

export const slotsQuerySchema = z.object({
  date: isoDate,
  guests: z.array(guestRequestSchema).min(1).max(10),
  /** Staff booking can see times outside posted hours. */
  includeOutsideHours: z.boolean().default(false),
});

export const checkSchema = z.object({
  start: localDateTimeSchema,
  guests: z.array(guestRequestSchema).min(1).max(10),
});

export const createBookingSchema = z.object({
  bookerClientId: z.string().uuid(),
  source: z.enum(["staff", "walk_in"]).default("staff"),
  start: localDateTimeSchema,
  notes: z.string().trim().max(1000).default(""),
  guests: z
    .array(
      z.preprocess(
        oneService,
        guestBase.extend({
          clientId: z.string().uuid(),
          /** Price override in cents (needs "Override prices" and a reason). */
          priceCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
          priceReason: z.string().trim().max(300).default(""),
        }),
      ),
    )
    .min(1)
    .max(10),
  /** Book despite conflicts (double-booking), with a reason that's logged. */
  forceReason: z.string().trim().max(300).nullable().default(null),
});
export type CreateBookingInput = z.infer<typeof createBookingSchema>;

export const moveSchema = z.object({
  start: localDateTimeSchema,
  staffId: z.string().uuid().nullable().default(null),
  resourceIds: z.array(z.string().uuid()).max(10).nullable().default(null),
  forceReason: z.string().trim().max(300).nullable().default(null),
});

/** Edit a booked visit: services (each with its add-ons), nurse, time, and price. */
export const editAppointmentSchema = z.preprocess(
  oneService,
  z.object({
  services: servicesField,
  staffId: z.string().uuid().nullable().default(null),
  start: localDateTimeSchema,
  /** Null = the menu price. Anything else is an override (needs "Override prices" and a reason). */
  priceCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
  priceReason: z.string().trim().max(300).default(""),
  /** Why it's against the visit rules (two drips, or a medication twice), if it is. */
  servicesReason: z.string().trim().max(300).default(""),
  /** Save despite conflicts, with a reason that's logged. */
  forceReason: z.string().trim().max(300).nullable().default(null),
  /** Why (e.g. something booked wasn't given: "client declined"), kept in the log. */
  reason: z.string().trim().max(300).default(""),
  /** Event visits: why something not on the event's menu is added (staff only), logged. */
  offMenuReason: z.string().trim().max(300).default(""),
  }),
);
export type EditAppointmentInput = z.infer<typeof editAppointmentSchema>;

export const statusChangeSchema = z.object({
  status: z.enum(APPOINTMENT_STATUSES),
  reason: z.string().trim().max(300).default(""),
  /** Starting a visit with forms unsigned needs a reason. */
  formsReason: z.string().trim().max(300).optional(),
  /** Starting a visit without a current good faith exam needs a reason. */
  gfeReason: z.string().trim().max(300).optional(),
  /** Clinic no-shows and late cancellations charge the fee to the card on file unless this is false. */
  chargeFee: z.boolean().optional(),
});

export const goAheadSchema = z.object({
  formsReason: z.string().trim().min(1).max(300).optional(),
  gfeReason: z.string().trim().min(1).max(300).optional(),
});

export interface SlotGuest {
  start: string;
  end: string;
  staffId: string;
  staffName: string;
  resourceIds: string[];
}
export interface Slot {
  /** Clinic-local start of the first guest. */
  start: string;
  guests: SlotGuest[];
}

/** One service on a visit, priced (after any group discount), with its add-ons. */
export interface PricedService {
  serviceId: string;
  name: string;
  listPriceCents: number;
  discountCents: number;
  priceCents: number;
  addOns: { id: string; name: string; priceCents: number; included: boolean }[];
  /** Online, for a signed-in client: a membership or package voucher covers this service (the plan's name). */
  coveredBy?: string | null;
}

/** A service booked on a visit (`id` is its line on the visit). */
export interface VisitServiceView {
  id: string;
  serviceId: string;
  name: string;
  priceCents: number;
  addOns: { id: string; name: string; priceCents: number }[];
}

export interface PriceLine {
  /** Each service on the visit. The fields below are the visit's totals. */
  services: PricedService[];
  /** The services' names, joined ("The Base + B12 Injection"). */
  serviceName: string;
  listPriceCents: number;
  discountCents: number;
  addOns: { id: string; name: string; priceCents: number; included: boolean }[];
  totalCents: number;
  /** Online, for a signed-in client: a membership or package voucher covers the service (the plan's name). */
  coveredBy?: string | null;
  /** Online: member pricing expected at checkout (on the add-ons, and the service when it isn't covered). */
  memberDiscountCents?: number;
  memberDiscountLabel?: string | null;
}

export interface CheckResult {
  ok: boolean;
  problems: { kind: Problem["kind"]; guest?: number; message: string }[];
  guests: SlotGuest[];
  prices: PriceLine[];
}

export interface AppointmentView {
  id: string;
  bookingId: string;
  bookingReference: string;
  /** "boulevard": synced from Boulevard, which owns its time, nurse, and services until it starts. */
  bookingSource: "staff" | "online" | "walk_in" | "boulevard";
  /** Synced from Boulevard, not taken over or started here: Boulevard owns its time, nurse, services, and cancelling. */
  managedInBoulevard: boolean;
  /** Synced from Boulevard: what the sync flagged for someone to check (open ones only). */
  boulevardFlags: { id: string; kind: BoulevardFlagKind; message: string }[];
  /** Synced from Boulevard and finished there (never started here): its forms and checkout were done in Boulevard. */
  finishedInBoulevard: boolean;
  /** Its checkout as Boulevard recorded it (visits finished in Boulevard, once read). */
  boulevardReceipt: BoulevardReceiptView | null;
  /** Its chart from Boulevard (finished there, once brought in), and whether the medical director has reviewed it. */
  boulevardChart: { id: string; review: "waiting" | "reviewed" | "not_needed" } | null;
  context: "clinic" | "mobile" | "event";
  /** Mobile requests stay "requested" until a nurse or manager answers. */
  bookingStatus: "pending" | "requested" | "confirmed" | "declined" | "expired";
  /** An online booking waiting for its card or deposit: the time is held until then (then it's let go). */
  holdUntil: string | null;
  /** Mobile: one-line address. */
  address: string | null;
  /** Events: the event's name. */
  eventName: string | null;
  client: { id: string; firstName: string; lastName: string; preferredName: string | null; dob: string | null; mobile: string | null; schedulingAlert: string | null; minorWithoutGuardian: boolean };
  booker: { id: string; firstName: string; lastName: string };
  /** The first service (its colour on the calendar); every service is in `services`. */
  serviceId: string;
  /** The services' names, joined. */
  serviceName: string;
  /** The services on the visit, in order, each with the add-ons that ride on it. */
  services: VisitServiceView[];
  color: string | null;
  staffId: string | null;
  /** Clinic-local times. */
  start: string;
  /** When the nurse is first busy (mobile: travel before the visit). */
  staffFrom: string;
  staffEnd: string;
  end: string;
  resourcesFree: string;
  status: AppointmentStatus;
  addOns: { id: string; name: string; priceCents: number }[];
  resourceIds: string[];
  priceCents: number;
  listPriceCents: number;
  discountCents: number;
  priceOverrideReason: string | null;
  /** Booked against the visit rules (two drips, or a medication twice): staff's reason. */
  servicesOverrideReason: string | null;
  /** Booked online by someone who says they're this child's parent or guardian but isn't linked: staff check. */
  guardianCheck: { clientId: string; name: string } | null;
  /** A membership or package voucher pays for it (the plan's name). */
  coveredBy: string | null;
  /** Forms this visit still needs signed (empty when all are in, or the visit is cancelled). */
  formsNeeded: FormNeed[];
  /** Good faith exam on the visit date (null when the service doesn't need one, or the visit is over). */
  gfe: GfeSummary | null;
  /** The visit's charts: the current one (an updated chart in place of the one it replaced), and adverse reaction reports. */
  charts: { id: string; templateKey: ChartTemplateKey; status: "draft" | "signed" }[];
  /** Billed at checkout but not on the signed chart: the nurse updates the chart. */
  chartNeedsUpdate: string[];
}

export interface CalendarResponse {
  timezone: string;
  from: string;
  to: string;
  staff: { id: string; name: string }[];
  units: { id: string; name: string; typeId: string; typeName: string }[];
  appointments: AppointmentView[];
  /** Per staff member per date: open periods (minutes after midnight) and where. */
  availability: { staffId: string; date: string; periods: { start: number; end: number }[] }[];
  /** Posted hours per date (minutes after midnight); empty when closed. */
  openHours: { date: string; periods: { start: number; end: number }[] }[];
  blocks: { resourceId: string | null; typeId: string | null; start: string; end: string; reason: string }[];
  /** Nurses working events (away from the clinic). */
  eventShifts: { staffId: string; occurrenceId: string; eventId: string; eventName: string; start: string; end: string }[];
}

export interface AppointmentDetail extends AppointmentView {
  /** Event visits: what's on the event's menu (staff can add others, with a reason). */
  eventMenuIds: string[] | null;
  /** Event visits: the nurses working that event date (the nurse can be changed to one of them). */
  eventStaffIds: string[] | null;
  /** False when nothing on the visit goes on a chart (only recovery, or items not on charts): no chart is started or expected. */
  needsChart: boolean;
  /** The nurse's name (null: unassigned). */
  staffName: string | null;
  history: { from: string | null; to: string; reason: string; by: string | null; at: string }[];
  bookingAppointments: { id: string; clientName: string; serviceName: string; start: string; status: AppointmentStatus }[];
  notes: string;
  /** Reasons given to treat without signed forms or a current good faith exam. */
  goAhead: { forms: string | null; gfe: string | null };
  /** Private forms link for this client (opened with their date of birth). */
  formsLink: string;
  health: HealthSummary;
  /** The booker's card on file, the booking's deposit, and money so far (deposits, fees, payments). */
  card: SavedCardView | null;
  deposit: { cents: number; status: "none" | "due" | "held" | "paid" | "released" | "forfeited" | "refunded" };
  payments: PaymentView[];
  /** Would cancelling now (or a no-show) charge the clinic fee, and how much. */
  feeIfLate: number | null;
  lateCancel: boolean;
  /** The bill's discount (the same one checkout shows): dollars off and why; open = it can still change. */
  billDiscount: { cents: number; reason: string | null; open: boolean };
}

/** An online booking waiting for its card or deposit holds its time this long, then it's let go. */
export const ONLINE_HOLD_MINUTES = 40;
